import React, { useEffect, useRef } from "react";

// ─── Camada visual compartilhada da "família landing" ──────────
// Extraído de LandingPage.jsx pra ser reaproveitado também pela página
// pública de compra de leads (ComprarLeadsPage.jsx): bolhas subindo, badges
// flutuantes com parallax/repulsão do mouse, e a inclinação 3D do mockup
// (celular na LandingPage, notebook na página de leads).
//
// `mockupRef` é um ref genérico que a página-mãe cria com useRef() e anexa
// ao SEU próprio elemento de mockup (dentro de `children`) — este componente
// só lê mockupRef.current pra aplicar o tilt, não é dono do elemento.
export default function LandingBackgroundFX({ chipData, mockupRef, regionRef, children }) {
  const layerRef = useRef(null);

  // Se a página-mãe passar `regionRef`, a camada de badges acompanha a
  // altura REAL desse elemento em tempo real (via ResizeObserver) — necessário
  // na ComprarLeadsPage porque o card do formulário muda MUITO de altura
  // (formulário completo vs. caixa curta do QR code do Pix). Uma altura fixa
  // nunca serve pros dois casos ao mesmo tempo.
  useEffect(() => {
    const layer = layerRef.current;
    const region = regionRef?.current;
    if (!layer || !region) return;

    const sync = () => {
      layer.style.top = region.offsetTop + "px";
      layer.style.height = region.offsetHeight + "px";
    };
    sync();

    const ro = new ResizeObserver(sync);
    ro.observe(region);
    window.addEventListener("resize", sync);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", sync);
    };
  }, [regionRef]);

  useEffect(() => {
    const layer = layerRef.current;
    const mockup = mockupRef?.current;
    if (!layer) return;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const chips = (chipData || []).map((d) => {
      const el = document.createElement("div");
      el.className = "lp-chip";
      el.innerHTML = (d.dot ? '<span class="lp-dot"></span>' : "") + d.txt;
      el.style.left = d.x + "%";
      el.style.top = d.y + "%";
      layer.appendChild(el);
      return { el, depth: d.depth, rx: 0, ry: 0 };
    });

    let bubbleTimer = null;
    if (!reduce) {
      const makeBubble = () => {
        const b = document.createElement("div");
        b.className = "lp-bubble";
        const size = Math.random() * 20 + 8;
        b.style.width = size + "px";
        b.style.height = size + "px";
        b.style.left = Math.random() * 100 + "%";
        b.style.bottom = "-40px";
        const dur = Math.random() * 6 + 5;
        b.style.animation = "lp-rise " + dur + "s linear forwards";
        layer.appendChild(b);
        setTimeout(() => b.remove(), dur * 1000);
      };
      bubbleTimer = setInterval(makeBubble, 650);
    }

    const mouse = { x: 0, y: 0, px: -9999, py: -9999 };
    const cur = { x: 0, y: 0 };
    const onMove = (e) => {
      mouse.x = e.clientX / window.innerWidth - 0.5;
      mouse.y = e.clientY / window.innerHeight - 0.5;
      mouse.px = e.clientX;
      mouse.py = e.clientY;
    };
    window.addEventListener("mousemove", onMove);

    let raf = null;
    const animate = () => {
      cur.x += (mouse.x - cur.x) * 0.06;
      cur.y += (mouse.y - cur.y) * 0.06;
      if (mockup && !reduce) {
        mockup.style.transform =
          "rotateY(" + cur.x * 24 + "deg) rotateX(" + -cur.y * 16 + "deg)";
      }
      chips.forEach((c) => {
        const rect = c.el.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = mouse.px - cx;
        const dy = mouse.py - cy;
        const dist = Math.sqrt(dx * dx + dy * dy);
        let tx = cur.x * 50 * c.depth;
        let ty = cur.y * 50 * c.depth;
        if (dist < 210 && dist > 0) {
          const force = (210 - dist) / 210;
          tx += (dx / dist) * force * -55;
          ty += (dy / dist) * force * -55;
        }
        c.rx += (tx - c.rx) * 0.1;
        c.ry += (ty - c.ry) * 0.1;
        c.el.style.transform = "translate(" + c.rx + "px, " + c.ry + "px)";
      });
      raf = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      window.removeEventListener("mousemove", onMove);
      if (raf) cancelAnimationFrame(raf);
      if (bubbleTimer) clearInterval(bubbleTimer);
      chips.forEach((c) => c.el.remove());
    };
  }, [chipData, mockupRef]);

  return (
    <>
      <style>{LP_SHARED_CSS}</style>
      <div className="lp-float" ref={layerRef} />
      {children}
    </>
  );
}

// CSS compartilhado entre LandingPage e ComprarLeadsPage: variáveis de tema,
// fundo, bolhas/badges, botões e o "palco" (glow + stage) do mockup. Cada
// página injeta o seu próprio CSS por cima disso pro conteúdo específico
// (celular vs. notebook, seções de marketing vs. formulário de filtros).
export const LP_SHARED_CSS = `
.lp-root{--lp-accent:#2563EB;--lp-accent2:#3b82f6;--lp-text:#f4f7fb;--lp-muted:rgba(244,247,251,.62);--lp-glass:rgba(255,255,255,.05);--lp-border:rgba(255,255,255,.12);position:relative;min-height:100vh;display:flow-root;overflow-x:clip;color:var(--lp-text);font-family:-apple-system,BlinkMacSystemFont,"Inter",sans-serif;background:radial-gradient(circle at 50% 32%,#13294d 0%,#0c1730 48%,#060b18 100%);}
.lp-root *{box-sizing:border-box;}
.lp-float{position:absolute;top:0;left:0;width:100%;max-width:100vw;height:100vh;pointer-events:none;z-index:0;overflow:hidden;}
.lp-chip{position:absolute;display:flex;align-items:center;gap:.5rem;padding:.55rem .9rem;background:var(--lp-glass);border:1px solid var(--lp-border);border-radius:13px;backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);font-size:.8rem;font-weight:500;white-space:nowrap;color:var(--lp-text);box-shadow:0 12px 38px rgba(0,0,0,.35);transition:transform .7s cubic-bezier(.2,.8,.2,1);will-change:transform;}
.lp-dot{width:8px;height:8px;border-radius:50%;background:var(--lp-accent2);flex-shrink:0;}
.lp-bubble{position:absolute;border-radius:50%;background:radial-gradient(circle at 30% 30%,rgba(255,255,255,.16),rgba(255,255,255,.02));border:1px solid rgba(255,255,255,.07);pointer-events:none;}
@keyframes lp-rise{0%{transform:translateY(0) translateX(0);opacity:0;}12%{opacity:.45;}88%{opacity:.45;}100%{transform:translateY(-110vh) translateX(40px);opacity:0;}}
.lp-eyebrow{display:inline-flex;align-items:center;gap:.5rem;font-family:"JetBrains Mono",ui-monospace,monospace;font-size:.72rem;letter-spacing:.12em;text-transform:uppercase;color:var(--lp-accent2);border:1px solid rgba(59,130,246,.28);border-radius:100px;padding:.4rem .9rem;margin-bottom:1.5rem;background:rgba(59,130,246,.07);}
.lp-pulse{width:7px;height:7px;border-radius:50%;background:var(--lp-accent2);animation:lp-pulse 2s infinite;}
@keyframes lp-pulse{0%{box-shadow:0 0 0 0 rgba(59,130,246,.5);}70%{box-shadow:0 0 0 9px rgba(59,130,246,0);}100%{box-shadow:0 0 0 0 rgba(59,130,246,0);}}
.lp-btn-primary{display:inline-flex;align-items:center;gap:.8rem;background:var(--lp-accent);color:#fff;border:none;padding:.95rem 1.4rem .95rem 1.8rem;border-radius:100px;font-weight:700;font-size:.95rem;cursor:pointer;transition:transform .25s ease,box-shadow .25s ease;box-shadow:0 10px 30px rgba(37,99,235,.4);}
.lp-btn-primary:hover{transform:translateY(-3px);box-shadow:0 16px 38px rgba(37,99,235,.55);}
.lp-arrow{width:30px;height:30px;border-radius:50%;background:rgba(255,255,255,.18);color:#fff;display:grid;place-items:center;font-size:1rem;}
.lp-btn-ghost{background:transparent;color:var(--lp-text);border:1px solid var(--lp-border);padding:.95rem 1.6rem;border-radius:100px;font-weight:600;font-size:.95rem;cursor:pointer;transition:all .25s ease;}
.lp-btn-ghost:hover{background:var(--lp-glass);border-color:rgba(255,255,255,.3);}
.lp-center{margin:0 auto;}
.lp-stage{position:relative;display:grid;place-items:center;perspective:1100px;min-height:600px;}
.lp-glow{position:absolute;width:440px;height:440px;border-radius:50%;background:radial-gradient(circle,rgba(37,99,235,.22) 0%,transparent 68%);filter:blur(8px);}
@keyframes lp-floaty{0%,100%{margin-top:0;}50%{margin-top:-18px;}}
@media (max-width:980px){.lp-chip{display:none;}}
`;
